import { consume } from '@lit/context'
import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import { combineLatest } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { SchmancyStepsMode, StepsController, stepsContext } from './steps.context'
import '../typography/typography'

const checkPath =
	'M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z'

const FOCUS_RING =
	'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-default'

@customElement('schmancy-step')
export class SchmancyStep extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}
			/* The trailing run-gap suppression lives on the HOST: each <li> is
			   alone in its own shadow root, so a last: (":last-child") variant on
			   the <li> itself matches every step and zeroes the run spacing
			   list-wide. Only the list's final step drops its padding. */
			:host(:last-of-type) li {
				padding-bottom: 0;
			}
		`,
	]

	@property({ type: String }) label = ''
	@property({ type: String }) description = ''

	/** Forces the step non-navigable. Can only remove the navigability a `complete` or `reached` status grants — it never grants any. */
	@property({ type: Boolean, reflect: true }) disabled = false

	/**
	 * The reader got past this step without finishing it — they skipped it. Behind
	 * the furthest step it reads `reached` instead of `complete`: ringed, numbered,
	 * navigable, never ticked. Inert everywhere else.
	 */
	@property({ type: Boolean, reflect: true }) incomplete = false

	@consume({ context: stepsContext })
	private steps!: StepsController

	/** 1-based DOM-order position — set only by <schmancy-steps>'s slotchange handler via setIndex(). Not public API. */
	@state()
	private index = 0

	/** Local reactive copy of the controller's current-step channel. */
	@state()
	private value = 1

	/** Local reactive copy of the furthest-reached channel; null = "as far as `value`". */
	@state()
	private reached: number | null = null

	private mode: SchmancyStepsMode = 'content'

	connectedCallback() {
		super.connectedCallback()
		this.mode = this.steps.mode
		combineLatest([this.steps.value$, this.steps.reached$])
			.pipe(takeUntil(this.disconnecting))
			.subscribe(([value, reached]) => {
				this.value = value
				this.reached = reached
				this.updateFlexProperties()
			})
	}

	firstUpdated() {
		this.updateFlexProperties()
	}

	/** Not public API — <schmancy-steps> assigns DOM-order position on slotchange. */
	setIndex(index: number) {
		this.index = index
	}

	private updateFlexProperties() {
		this.style.flex = this.mode === 'content' && this.status === 'current' ? '1 1 auto' : '0 0 auto'
	}

	/**
	 * The furthest step the reader has been to. A `reached` below `value` cannot
	 * pull it back: the reader is standing on `value`, so they got at least that
	 * far.
	 */
	private get furthest(): number {
		return Math.max(this.value, this.reached ?? this.value)
	}

	/**
	 * Completion is measured against `furthest`, never against `value` alone —
	 * stepping back must not un-complete what is already answered. With `reached`
	 * unset `furthest` collapses to `value` and the step there is the current
	 * one, so a forward-only stepper reads `reached` only on an `incomplete` step
	 * — passed, not done.
	 */
	get status(): 'complete' | 'current' | 'reached' | 'upcoming' {
		if (this.index === this.value) return 'current'
		if (this.index < this.furthest) return this.incomplete ? 'reached' : 'complete'
		if (this.index === this.furthest) return 'reached'
		return 'upcoming'
	}

	/** The furthest step is somewhere to return to without being done, so navigability runs one step wider than completion. */
	get navigable(): boolean {
		return (this.status === 'complete' || this.status === 'reached') && !this.disabled
	}

	private onStepClick() {
		if (!this.navigable) return
		this.steps.requestNavigate(this.index)
	}

	private markerClasses(sizeClass: string) {
		return {
			[`relative z-10 flex ${sizeClass} shrink-0 items-center justify-center rounded-full transition-colors duration-200`]: true,
			'bg-primary-default text-primary-on': this.status === 'current',
			'bg-primary-container text-primary-onContainer': this.status === 'complete',
			// Both outlined statuses share ONE key: `this.classMap` splits multi-token
			// keys, so `border` written in two keys is one token written twice — the
			// last branch wins and the true one loses its border. `border-solid` is
			// explicit for a separate reason: Tailwind's `--tw-border-style`
			// @property initial does not resolve in this adopted shadow-root sheet,
			// so bare `border` computes to 0px none without it.
			'border border-solid': this.status === 'reached' || this.status === 'upcoming',
			'border-primary-default text-primary-default': this.status === 'reached',
			'border-outlineVariant': this.status === 'upcoming',
		}
	}

	private labelClasses() {
		return {
			'text-surface-on font-medium': this.status === 'current',
			'text-surface-onVariant font-normal': this.status !== 'current',
		}
	}

	/** The connector below a step traces the path already walked, so a current step the reader stepped back to keeps its run filled. */
	private connectorClasses() {
		return {
			'bg-outline': this.index < this.furthest,
			'bg-outlineVariant': this.index >= this.furthest,
		}
	}

	private renderMarker(markerSize: string, checkSize: string) {
		return html`
			<span class=${this.classMap(this.markerClasses(markerSize))}>
				${
					this.status === 'complete'
						? html`
								<svg class="${checkSize}" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
									<path fill-rule="evenodd" d=${checkPath} clip-rule="evenodd" />
								</svg>
							`
						: html`<span
								class="text-xs leading-none ${
									this.status === 'upcoming' ? 'font-normal text-surface-onVariant' : 'font-medium'
								}"
								>${this.index}</span
							>`
				}
			</span>
		`
	}

	render() {
		const isLast = !this.nextElementSibling?.matches('schmancy-step')

		return this.mode === 'content'
			? this.renderContentRow(this.navigable, isLast)
			: this.renderRailRow(this.navigable, isLast)
	}

	private renderContentRow(navigable: boolean, isLast: boolean) {
		return html`
			<li class="relative pb-12">
				${when(
					!isLast,
					() => html`
						<div
							class="absolute left-3 -translate-x-1/2 top-6 h-[calc(100%-1.5rem)] w-0.5 rounded-full transition-colors duration-200 ${this.classMap(
								this.connectorClasses(),
							)}"
							aria-hidden="true"
						></div>
					`,
				)}

				<button
					type="button"
					?disabled=${!navigable}
					aria-current=${ifDefined(this.status === 'current' ? 'step' : undefined)}
					aria-label=${ifDefined(this.ariaLabel || undefined)}
					@click=${this.onStepClick}
					class="group relative flex items-start gap-3 rounded-full -mx-2 px-2 -my-1 py-1 enabled:cursor-pointer ${FOCUS_RING}"
				>
					${this.renderMarker('size-6', 'size-3.5')}

					<span class="flex flex-col items-start justify-center min-w-0 gap-0.5">
						<schmancy-typography type="title" token="md" class="h-6 flex items-center">
							<span
								class=${this.classMap({
									...this.labelClasses(),
									'transition-colors duration-200 group-enabled:group-hover:underline group-enabled:group-hover:text-surface-on': true,
								})}
								>${this.label}</span
							>
						</schmancy-typography>
						${when(
							this.description,
							() => html`
								<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
									${this.description}
								</schmancy-typography>
							`,
						)}
					</span>
				</button>

				${when(
					this.status === 'current',
					() => html`
						<div class="pl-9 pt-4 pb-2">
							<slot></slot>
						</div>
					`,
					() => html`<slot class="hidden"></slot>`,
				)}
			</li>
		`
	}

	private renderRailRow(navigable: boolean, isLast: boolean) {
		return html`
			<li class="relative pb-8">
				${when(
					!isLast,
					() => html`
						<div
							class="absolute left-2.5 -translate-x-1/2 top-5 h-[calc(100%-1.25rem)] w-0.5 rounded-full transition-colors duration-200 ${this.classMap(
								this.connectorClasses(),
							)}"
							aria-hidden="true"
						></div>
					`,
				)}

				<button
					type="button"
					?disabled=${!navigable}
					aria-current=${ifDefined(this.status === 'current' ? 'step' : undefined)}
					aria-label=${ifDefined(this.ariaLabel || undefined)}
					@click=${this.onStepClick}
					class="group relative flex items-center gap-2 rounded-full -mx-2 px-2 -my-1 py-1 enabled:cursor-pointer ${FOCUS_RING}"
				>
					${this.renderMarker('size-5', 'size-3')}

					<schmancy-typography type="label" token="lg" class="h-5 flex items-center">
						<span
							class=${this.classMap({
								...this.labelClasses(),
								'transition-colors duration-200 group-enabled:group-hover:underline group-enabled:group-hover:text-surface-on': true,
							})}
							>${this.label}</span
						>
					</schmancy-typography>
				</button>
			</li>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-step': SchmancyStep
	}
}
